import { useFormikContext } from 'formik'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { SignupFormValues } from '@context/SignupContext'

import { Heading6 } from '@ui/Typography'
import { Button } from '@ui/components/Button'
import { ArrowIcon } from '@ui/icons/Arrow.icon'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { useWindowResize } from '@utils/domUtils'

import { CountryPreferenceStrippedDto } from '@model/CountryDto'

import { useScrollToTop } from '@hooks/useScrollToTop'

import { PhoneNumberInput } from '@shared/components/PhoneNumberInput/PhoneNumberInput'

import styles from './UpdatePhoneForm.module.scss'

interface UpdatePhoneFormProps {
  countryCode: string
  countries: CountryPreferenceStrippedDto[]

  onSubmit(phoneNumber: string, countryCode: string): Promise<void>
  onCancel(): void
}

export const UpdatePhoneForm: React.FC<UpdatePhoneFormProps> = (props) => {
  const { countryCode, onSubmit, onCancel, countries } = props

  useScrollToTop()

  const { t } = useTranslation()
  const isMobile = useWindowResize()

  const apiClient = useApiClient(ClientApiClient)
  const delayDebounceFn = useRef<NodeJS.Timeout>(undefined)
  const { setFieldValue, values } = useFormikContext<SignupFormValues>()

  const handleOnChangeOption = (country: CountryPreferenceStrippedDto) => {
    setIsValid(false)
    setFieldValue('countryCode', country.phoneCode)
    validatePhoneNumber(values.phoneNumber, country.phoneCode)
  }

  const [phoneNumberError, setPhoneNumberError] = useState<string>()
  const [isValid, setIsValid] = useState(false)

  useEffect(() => {
    setFieldValue('countryCode', countryCode)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countries, countryCode])

  const initialCountry = useMemo(
    () => countries.find((c) => c.phoneCode === countryCode),
    [countries, countryCode]
  )

  const validatePhoneNumber = async (phoneNumber: string, countryCode?: string) => {
    try {
      await apiClient.validatePhoneNumber(phoneNumber, countryCode || values.countryCode)
      setPhoneNumberError(undefined)
      setIsValid(true)
    } catch {
      setPhoneNumberError(t('Sign up.Please check your phone number'))
      setIsValid(false)
    }
  }

  const handleChangePhoneNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsValid(false)
    setFieldValue('phoneNumber', e.target.value)
    if (!e.target.value) {
      setIsValid(false)
      setPhoneNumberError(undefined)
      clearTimeout(delayDebounceFn.current)
      return
    }
    clearTimeout(delayDebounceFn.current)
    delayDebounceFn.current = setTimeout(async () => validatePhoneNumber(e.target.value), 1000)
  }

  return (
    <div className={styles.updatePhone}>
      <Heading6 medium>{t('Sign up.Phone number')}</Heading6>
      <div className={styles.phoneWrapper}>
        <PhoneNumberInput
          name='phoneNumber'
          handleChangePhoneNumber={handleChangePhoneNumber}
          values={values.phoneNumber}
          countryCode={values.countryCode}
          countries={countries}
          initialCountry={initialCountry}
          handleOnChangeOption={handleOnChangeOption}
          phoneNumberError={phoneNumberError}
        />
      </div>
      <div className='is-flex is-flex-wrap-wrap'>
        {isMobile ? (
          <div className='is-flex-grow-1 is-flex-direction-column'>
            <span className='is-flex ml-1 mb-2 is-flex-grow-1'>
              <SubmitButton onSubmit={onSubmit} isValid={isValid} values={values} />
            </span>
            <span className='is-flex mr-1 mb-2 is-flex-grow-1'>
              <CancelButton onCancel={onCancel} />
            </span>
          </div>
        ) : (
          <div className='is-flex is-flex-grow-1'>
            <span className='is-flex mr-1 mb-2 is-flex-grow-1'>
              <CancelButton onCancel={onCancel} />
            </span>
            <span className='is-flex ml-1 mb-2 is-flex-grow-1'>
              <SubmitButton onSubmit={onSubmit} isValid={isValid} values={values} />
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

interface SubmitButtonProps {
  onSubmit(phoneNumber: string, countryCode: string): Promise<void>
  isValid: boolean
  values: SignupFormValues
}

const SubmitButton: React.FC<SubmitButtonProps> = (props) => {
  const { onSubmit, isValid, values } = props

  const { t } = useTranslation()

  return (
    <div className='is-flex-grow-1'>
      <Button.Primary
        size='lg'
        iconRight={ArrowIcon.Right}
        disabled={!isValid}
        onClick={() => onSubmit(values.phoneNumber, values.countryCode)}
      >
        {t('Next')}
      </Button.Primary>
    </div>
  )
}

const CancelButton: React.FC<{
  onCancel(): void
}> = (props) => {
  const { onCancel } = props

  const { t } = useTranslation()

  return (
    <div className='is-flex-grow-1'>
      <Button.Secondary size='lg' onClick={onCancel}>
        {t('Cancel')}
      </Button.Secondary>
    </div>
  )
}
